/**
 * AccountSelector component with dark theme styling.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select } from '@/components/ui';
import type { Account } from '@/types';

interface AccountSelectorProps {
  accounts: Account[];
  selectedAccountId: string | null;
  onSelect: (accountId: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export function AccountSelector({
  accounts,
  selectedAccountId,
  onSelect,
  isLoading = false,
  disabled = false,
}: AccountSelectorProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isUserSelection = useRef(false);
  const lastSyncedAccountId = useRef<string | null>(null);

  // Sync URL to state on initial load only
  useEffect(() => {
    if (accounts.length === 0) return;
    
    const urlAccountId = searchParams.get('account');
    if (urlAccountId && !selectedAccountId) {
      const accountExists = accounts.some((a) => a.account_id === urlAccountId);
      if (accountExists) {
        lastSyncedAccountId.current = urlAccountId;
        onSelect(urlAccountId);
      }
    }
  }, [accounts, selectedAccountId, onSelect, searchParams]);

  // Sync state to URL when selectedAccountId changes
  useEffect(() => {
    if (!selectedAccountId) return;
    if (lastSyncedAccountId.current === selectedAccountId) return;
    
    lastSyncedAccountId.current = selectedAccountId;
    const currentParam = searchParams.get('account');
    if (currentParam !== selectedAccountId) {
      setSearchParams({ account: selectedAccountId }, { replace: true });
    }
  }, [selectedAccountId, searchParams, setSearchParams]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const accountId = e.target.value;
    if (accountId && accountId !== selectedAccountId) {
      isUserSelection.current = true;
      lastSyncedAccountId.current = accountId;
      onSelect(accountId);
      setSearchParams({ account: accountId }, { replace: true });
    }
  }, [selectedAccountId, onSelect, setSearchParams]);

  const options = accounts.map((account) => ({
    value: account.account_id,
    label: account.name,
  }));

  return (
    <div className="w-64">
      <Select
        value={selectedAccountId ?? ''}
        onChange={handleChange}
        options={options}
        placeholder={isLoading ? 'Loading accounts...' : 'Select an account'}
        disabled={disabled || isLoading || accounts.length === 0}
      />
    </div>
  );
}
